import {
  chaveDoDiaEmSaoPaulo,
  ehDiaDeCalendario,
  ehHoraDoDia,
  formatDateTimeAt,
  instanteEmSaoPaulo,
} from '@/shared/formatters/date';
import { ehErroHttp, temCodigoHttp } from '@/shared/utils/erroHttp';
import { rotuloMeioCitacao } from '@/shared/utils/rotulosCitacaoRequerido';

import type {
  DocumentoArbitralItem,
  FormularioRegistroCitacao,
  MeioCitacaoRegistro,
  ParteCompletaItem,
  RegistrarCitacaoInput,
} from '@/features/secretaria-arbitral/types';

import { motivoAcaoLegivel } from './motivoLoteLabels';
import { parteEstaAtiva } from './partesAcesso';
import { JUSTIFICATIVA_MAX } from './partesEscrita';

/**
 * Espelha `JUSTIFICATIVA_REGISTRO_CITACAO_MIN_CHARS` do core, maior que o das outras escritas de
 * parte (10): a justificativa é a única narrativa de por que a Secretaria deu a parte por citada.
 */
export const JUSTIFICATIVA_REGISTRO_CITACAO_MIN = 20;

// Os rótulos moram em `shared/` porque o Certificado de Acessos imprime o mesmo meio (C37).
export const MEIOS_REGISTRO_CITACAO: Record<MeioCitacaoRegistro, string> = {
  AR: rotuloMeioCitacao('AR'),
  CLAUSULA_CONTRATUAL: rotuloMeioCitacao('CLAUSULA_CONTRATUAL'),
  WHATSAPP: rotuloMeioCitacao('WHATSAPP'),
  EMAIL: rotuloMeioCitacao('EMAIL'),
};

export const FORMULARIO_REGISTRO_CITACAO_VAZIO: FormularioRegistroCitacao = {
  meio: '',
  dia: '',
  hora: '',
  documentoId: '',
  justificativa: '',
};

const PAPEL_REQUERIDO = 'REQUERIDO';

/**
 * Requerido ativo ainda não citado. `citado` vem pronto do core, calculado pela mesma régua que
 * o registro usa para recusar: repetir aqui a lista de status criaria uma segunda opinião sobre
 * "quem está citado", que divergiria na primeira mudança do enum.
 */
export function podeRegistrarCitacao(parte: ParteCompletaItem): boolean {
  return (
    parte.papeisVigentes.includes(PAPEL_REQUERIDO) &&
    parteEstaAtiva(parte) &&
    parte.detalheArbitral?.citado !== true
  );
}

/**
 * Por que o registro está bloqueado pela Lexi, ou `null`. Decide o botão DESABILITADO (com o
 * motivo escrito), não a visibilidade — essa é de `podeRegistrarCitacao`. A regra vem pronta do
 * core (`citacaoEmCursoNaLexi`, a mesma do 409): repetir aqui a lista de status seria uma segunda
 * opinião. Sem o campo (core antigo), não há impedimento e o 409 segue como defesa.
 *
 * Sem "até quando": a execução não guarda o horário do timeout, e um prazo calculado na tela
 * poderia passar sem a Lexi parar (runbook arbitral-flow-stuck, causa #7).
 */
export function impedimentoDeCitacaoPelaLexi(parte: ParteCompletaItem): string | null {
  const emCurso = parte.citacaoEmCursoNaLexi;
  if (!emCurso) return null;
  if (emCurso.motivo === 'DISPARO_SEM_EXECUCAO') {
    return 'A Lexi está iniciando a citação desta parte. Peça ao time técnico que confira antes de registrar.';
  }
  const inicio = formatDateTimeAt(emCurso.desde);
  const desde = inicio ? ` (conversa iniciada em ${inicio})` : '';
  return `A Lexi ainda está citando esta parte${desde}. Peça ao time técnico que encerre a conversa antes de registrar.`;
}

/** Só o que foi juntado por upload: o core recusa como evidência a peça emitida pela câmara. */
export function documentosElegiveisComoEvidencia(
  documentos: DocumentoArbitralItem[],
): DocumentoArbitralItem[] {
  return documentos.filter((documento) => documento.fonte === 'JUNTADO');
}

export function rotuloDoDocumentoDeEvidencia(documento: DocumentoArbitralItem): string {
  return documento.descricao?.trim() || documento.arquivo.nomeOriginal;
}

/** Dia e hora de um ato registrado à mão, como o formulário entrega. */
type DiaEHoraDoAto = Pick<FormularioRegistroCitacao, 'dia' | 'hora'>;

/** Sem hora, vai o dia nu — inventar meia-noite faria o ato aparecer um dia antes em Brasília. */
export function dataAtoDoFormulario(formulario: DiaEHoraDoAto): string {
  if (!formulario.hora) return formulario.dia;
  return instanteEmSaoPaulo(formulario.dia, formulario.hora);
}

function dataAtoEhFutura(formulario: DiaEHoraDoAto, agora: Date): boolean {
  if (!formulario.hora) return formulario.dia > (chaveDoDiaEmSaoPaulo(agora) ?? '');
  return new Date(dataAtoDoFormulario(formulario)).getTime() > agora.getTime();
}

/**
 * O que impede a data de um ato registrado à mão (vazia, inválida, hora inválida, futura), ou
 * `null`. Compartilhada pelos registros da citação e da intimação da sentença: as duas datas vão
 * ao core pelo mesmo validador (`dataAto`). `rotulo` é o complemento da frase ("do ato",
 * "da intimação").
 *
 * Total: dia ou hora fora do formato viram mensagem antes de chegar a `instanteEmSaoPaulo`, que
 * lança — roda no render.
 */
export function impedimentoDaDataDoAto(
  formulario: DiaEHoraDoAto,
  agora: Date,
  rotulo: string,
): string | null {
  if (!formulario.dia) return `Informe a data ${rotulo}.`;
  if (!ehDiaDeCalendario(formulario.dia)) return `Data ${rotulo} inválida.`;
  if (formulario.hora && !ehHoraDoDia(formulario.hora)) return `Hora ${rotulo} inválida.`;
  if (dataAtoEhFutura(formulario, agora)) return `A data ${rotulo} não pode ser futura.`;
  return null;
}

/** Mesmo piso das duas justificativas de registro manual, e o teto das escritas de parte. */
export function justificativaDoRegistroValida(texto: string): boolean {
  const tamanho = texto.trim().length;
  return tamanho >= JUSTIFICATIVA_REGISTRO_CITACAO_MIN && tamanho <= JUSTIFICATIVA_MAX;
}

/**
 * O que ainda impede confirmar, na ordem em que o formulário é preenchido, ou `null` — e é a ÚNICA
 * condição do botão: tudo que o desabilita aparece escrito no diálogo. Antecipa as recusas do core
 * (400) para o botão não ficar habilitado num pedido que vai voltar.
 */
export function impedimentoDoRegistroCitacao(
  formulario: FormularioRegistroCitacao,
  agora: Date,
): string | null {
  if (!formulario.meio) return 'Escolha o meio da citação.';
  const impedimentoDaData = impedimentoDaDataDoAto(formulario, agora, 'do ato');
  if (impedimentoDaData) return impedimentoDaData;
  if (formulario.meio === 'AR' && !formulario.documentoId) {
    return 'Citação por AR exige o AR digitalizado como evidência.';
  }
  if (!justificativaDoRegistroValida(formulario.justificativa)) {
    return `Escreva a justificativa com no mínimo ${JUSTIFICATIVA_REGISTRO_CITACAO_MIN} caracteres.`;
  }
  return null;
}

export function montarRegistroCitacaoInput(
  formulario: FormularioRegistroCitacao & { meio: MeioCitacaoRegistro },
): RegistrarCitacaoInput {
  return {
    meio: formulario.meio,
    dataAto: dataAtoDoFormulario(formulario),
    justificativa: formulario.justificativa.trim(),
    ...(formulario.documentoId ? { documentoId: formulario.documentoId } : {}),
  };
}

/**
 * As mensagens do core para estas recusas carregam ids e a lista de status esperados (são
 * diagnóstico); o operador precisa do que fazer.
 */
export function mensagemDoRegistroCitacao(erro: unknown): string {
  if (temCodigoHttp(erro, 'CITACAO_JA_CONFIRMADA')) {
    return 'Esta parte já conta como citada — atualize a página para ver a situação atual.';
  }
  if (temCodigoHttp(erro, 'PARTE_NAO_E_REQUERIDO_ATIVO')) {
    return 'Só a citação de requerido ativo pode ser registrada.';
  }
  if (temCodigoHttp(erro, 'CITACAO_EM_CURSO_NA_LEXI')) {
    return 'A conversa de citação da Lexi com esta parte ainda está em andamento. Peça ao time técnico que encerre a execução antes de registrar — senão a Lexi pode sobrescrever o registro.';
  }
  if (ehErroHttp(erro, 404)) {
    return 'O documento escolhido ou a parte não foi encontrado neste processo — atualize a página e tente de novo.';
  }
  return motivoAcaoLegivel(erro, 'Erro ao registrar a citação');
}
